import {
  DndContext,
  DragEndEvent,
  DragStartEvent,
  MouseSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import { useClickAway } from '@uidotdev/usehooks';
import { ReactNode, useEffect, useState } from 'react';

import { Box } from '@mui/system';

import useCalculateGridColumns from '@/shared/hooks/useCalculateGridColumns';

import SortableItem from './SortableItem';

type SortableItemType<T> = {
  id: string;
} & T;

type ComponentRendererType<T> = {
  props: T;
  index?: number;
  columnsLength?: number;
};

type WaveUISortableContainerProps<T> = {
  items: SortableItemType<T>[];
  componentRenderer: (props: ComponentRendererType<T>) => ReactNode;
  getUpdatedItems: (items: SortableItemType<T>[]) => void;
  getActiveItem?: (item: T | null) => void;
  onClickOutsideCallback?: (e: Event) => void;
};

const WaveUISortableContainer = <T,>({
  items,
  componentRenderer,
  getUpdatedItems,
  getActiveItem,
  onClickOutsideCallback,
}: WaveUISortableContainerProps<T>) => {
  const [activeItem, setActiveItem] = useState<T | null>(null);

  const mouseSensor = useSensor(MouseSensor, {
    activationConstraint: {
      distance: 10,
    },
  });
  const touchSensor = useSensor(TouchSensor, {
    activationConstraint: {
      distance: 10,
    },
  });

  const sensors = useSensors(mouseSensor, touchSensor);

  const ref = useClickAway<HTMLDivElement>((e) => onClickOutsideCallback?.(e));

  const { columnsLength, columnGap, columnWidth } = useCalculateGridColumns({
    containerRef: ref,
    columnGap: 16,
    columnWidth: 180,
  });

  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;
    const draggingItem = items.find((item) => item.id === active.id) || null;

    setActiveItem(draggingItem);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      const oldIndex = items.findIndex((item) => item.id === active.id);
      const newIndex = items.findIndex((item) => item.id === over?.id);

      getUpdatedItems(arrayMove(items, oldIndex, newIndex));
    }

    setActiveItem(null);
  };

  const handleDragCancel = () => setActiveItem(null);

  useEffect(() => {
    if (getActiveItem) {
      getActiveItem(activeItem);
    }
  }, [getActiveItem, activeItem]);

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <SortableContext items={items} strategy={rectSortingStrategy}>
        <Box
          ref={ref}
          sx={{
            display: 'grid',
            gridTemplateColumns: `repeat(auto-fill, minmax(${columnWidth}px, 1fr))`,
            gap: `${columnGap}px`,
          }}
        >
          {items.map((item, index) => (
            <SortableItem key={item.id} id={item.id}>
              {componentRenderer({ props: item, index, columnsLength })}
            </SortableItem>
          ))}
        </Box>
      </SortableContext>
    </DndContext>
  );
};

export default WaveUISortableContainer;
